import { useEffect, useMemo } from 'react';
import { useBoolean } from '@chakra-ui/react';

import useGetCompanyNumbers from '../setupProgress/queries/useGetCompanyNumbers';
import useGetCompanyUsers from '../setupProgress/queries/useGetCompanyUsers';
import useGetCompanyTrunks from '../queries/useGetCompanyTrunks';
import useGetNumbersByPlatform from '../queries/useGetNumbersByPlatform';
import useGetOperatorPlatform from 'pages/telephony/numbers/queries/useGetOperatorPlatform';
import {
    getNumberBreakdown,
    getServiceBreakdown,
    getTrunkBreakdown,
    getUserBreakdown,
} from './summaryBreakdowns';

// The rows, or null while loading or when the request failed, so the tile
// shows "-" rather than a misleading 0.
const rowsOf = (query, pick) => {
    const rows = query.isSuccess ? pick(query.data) : null;
    return Array.isArray(rows) ? rows : null;
};

/**
 * @typedef {object} SummaryBreakdown
 * @property {{ title: string, icon?: Function, image?: string, count: number }[]} items
 * @property {boolean} [isLoading]
 * @property {() => void} [onOpen] - loads the breakdown on first view
 * @property {string} [emptyText] - shown when it loaded with no items
 * @property {string} [note] - shown under the items
 */

/**
 * (3133) The account summary's headline counts for one company, and how they
 * split up. Numbers and users share the setup progress bar's queries, and
 * the operator platform's brand name the service sections' query. The
 * numbers' split takes a request per platform, so it is only fetched once
 * the Numbers tile is first opened.
 *
 * @param {object} params
 * @param {string} params.companyId
 * @param {object[]|null} params.services - dashboard services; null while unknown
 * @returns {{
 *   counts: { services: ?number, numbers: ?number, users: ?number, trunks: ?number },
 *   breakdowns: Record<string, SummaryBreakdown>,
 * }}
 */
const useAccountSummaryCounts = ({ companyId, services }) => {
    const numbersQuery = useGetCompanyNumbers(companyId);
    const usersQuery = useGetCompanyUsers(companyId);
    const trunksQuery = useGetCompanyTrunks(companyId);
    const { operatorPlatform } = useGetOperatorPlatform(
        companyId,
        Boolean(companyId),
    );
    const [isNumberBreakdownWanted, setIsNumberBreakdownWanted] =
        useBoolean(false);
    const numbersByPlatformQuery = useGetNumbersByPlatform(companyId, {
        enabled: isNumberBreakdownWanted,
    });

    // Another company waits for its own first look.
    useEffect(() => {
        setIsNumberBreakdownWanted.off();
    }, [companyId, setIsNumberBreakdownWanted]);

    const numbers = rowsOf(numbersQuery, (data) => data);
    const users = rowsOf(usersQuery, (data) => data?.tcapUsers);
    const trunks = rowsOf(trunksQuery, (data) => data);
    const operatorPlatformName = operatorPlatform?.name;

    const breakdowns = useMemo(
        () => ({
            services: {
                items: getServiceBreakdown(services, operatorPlatformName),
            },
            numbers: {
                items: getNumberBreakdown(
                    numbersByPlatformQuery.data,
                    operatorPlatformName,
                ),
                isLoading:
                    !isNumberBreakdownWanted ||
                    numbersByPlatformQuery.isInitialLoading,
                onOpen: setIsNumberBreakdownWanted.on,
                emptyText: numbersByPlatformQuery.isError
                    ? "Couldn't load numbers by platform"
                    : 'No numbers on a platform yet',
                note: 'A number on more than one platform counts under each.',
            },
            users: { items: getUserBreakdown(users, operatorPlatformName) },
            trunks: { items: getTrunkBreakdown(trunks) },
        }),
        [
            services,
            users,
            trunks,
            operatorPlatformName,
            isNumberBreakdownWanted,
            numbersByPlatformQuery.data,
            numbersByPlatformQuery.isInitialLoading,
            numbersByPlatformQuery.isError,
            setIsNumberBreakdownWanted.on,
        ],
    );

    return {
        counts: {
            services: Array.isArray(services) ? services.length : null,
            numbers: numbers?.length ?? null,
            users: users?.length ?? null,
            trunks: trunks?.length ?? null,
        },
        breakdowns,
    };
};

export default useAccountSummaryCounts;
